import React, { useState, useEffect } from 'react';
import { FormControl, FormHelperText, Typography, MenuItem, Select, InputLabel } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { translations, numberOfDamagedRoomsOptions, questionMaxLength, question500MaxLength } from '../../lib/consts';
import { GenericObject, doesValueHaveMaxTwoDecimalPlaces } from '@fnol/library/src/lib';
import { DecimalInput, YesNoSelector, TextField } from '@fnol/library/src/components';

type RoomProps = {
    id?: number;
};

const Room = ({ id }: RoomProps) => {
    const { values, errors, touched, setFieldValue } = useFormikContext<GenericObject>();

    const [requiredWallFinishType, setRequiredWallFinishType] = useState(false);

    const idRoom = '';
    const roomType = `room${idRoom}Type`;
    const area = `room${idRoom}Area`;
    const isDamageOnCeiling = `room${idRoom}IsDamageOnCeiling`;
    const numberOfDemagedWalls = `room${idRoom}NumberOfDemagedWalls`;
    const wallFinishType = `room${idRoom}WallFinishType`;
    const note = `room${idRoom}Note`;

    const handleChangeRoomType = (e: { target: { value: any } }): any => {
        setFieldValue(roomType, e.target.value);
    };
    const handleChangeWallFinishType = (e: { target: { value: any } }): any => {
        setFieldValue(wallFinishType, e.target.value);
    };
    const handleChangeNumberOfDemagedWalls = (e: { target: { value: any } }): any => {
        setFieldValue(numberOfDemagedWalls, e.target.value);
        if (e.target.value == '0') {
            setRequiredWallFinishType(false);
            setFieldValue(`room${idRoom}WallFinishType`, '');
        } else if (!requiredWallFinishType) {
            setRequiredWallFinishType(true);
        }
    };

    const handleChangeArea = (e: { target: { value: any } }): any => {
        if (doesValueHaveMaxTwoDecimalPlaces(e.target.value)) {
            setFieldValue(area, e.target.value);
        }
    };

    useEffect(() => {
        if (values.roomNumberOfDemagedWalls && values.roomNumberOfDemagedWalls !== '0') {
            setRequiredWallFinishType(true);
        }
    }, []);

    return (
        <>
            <FormControl error={!!(touched[roomType] && errors[roomType])} required>
                <InputLabel>{translations.room.TYPE}</InputLabel>
                <Select
                    id="roomType"
                    name="roomType"
                    value={values[roomType] || ''}
                    label={translations.room.TYPE}
                    onChange={handleChangeRoomType}
                >
                    {values.roomTypesList?.map((option: GenericObject) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.naziv}
                            </MenuItem>
                        );
                    })}
                </Select>
                {touched[roomType] && errors[roomType] ? (
                    <FormHelperText error={true}>
                        <>{errors[roomType]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl>
                <Field
                    name={area}
                    id={area}
                    label={translations.room.AREA}
                    as={DecimalInput}
                    onChange={handleChangeArea}
                    onKeyDown={(e: any) => e.keyCode === 69 && e.preventDefault()}
                    error={!!(touched[area] && errors[area])}
                    required
                />
                {touched[area] && errors[area] ? (
                    <FormHelperText error={true}>
                        <>{errors[area]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <Typography variant="subtitle1">{translations.room.IS_DAMAGE_ON_CEILING}</Typography>
            <FormControl>
                <YesNoSelector
                    name={isDamageOnCeiling}
                    id={isDamageOnCeiling}
                    idTrue={`${isDamageOnCeiling}True`}
                    idFalse={`${isDamageOnCeiling}False`}
                />
            </FormControl>
            <FormControl error={!!(touched.numberOfDemagedWalls && errors.numberOfDemagedWalls)} required>
                <InputLabel>{translations.room.NUMBER_OF_DAMAGED_WALL}</InputLabel>
                <Field
                    id={numberOfDemagedWalls}
                    name={numberOfDemagedWalls}
                    label={translations.room.NUMBER_OF_DAMAGED_WALL}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    as={Select}
                    onChange={handleChangeNumberOfDemagedWalls}
                >
                    {numberOfDamagedRoomsOptions.map((option) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        );
                    })}
                </Field>
                {touched[numberOfDemagedWalls] && errors[numberOfDemagedWalls] ? (
                    <FormHelperText error={true}>
                        <>{errors[numberOfDemagedWalls]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            {requiredWallFinishType && (
                <FormControl
                    error={!!(touched[wallFinishType] && errors[wallFinishType])}
                    required={requiredWallFinishType}
                >
                    <InputLabel>{translations.room.WALL_FINISH_TYPE}</InputLabel>
                    <Select
                        id="wallFinishType"
                        name="wallFinishType"
                        value={values[wallFinishType] || ''}
                        label={translations.room.WALL_FINISH_TYPE}
                        onChange={handleChangeWallFinishType}
                    >
                        {values.wallFinishTypesList?.map((option: GenericObject) => {
                            return (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.naziv}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {touched[wallFinishType] && errors[wallFinishType] ? (
                        <FormHelperText error={true}>
                            <>{errors[wallFinishType]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}
            <FormControl error={!!(touched.note && errors.note)}>
                <Field
                    id={note}
                    name={note}
                    multiline={true}
                    maxRows={3}
                    minRows={3}
                    label={translations.room.ROOM_NOTE}
                    as={TextField}
                    inputProps={{
                        maxLength: question500MaxLength,
                    }}
                    helperText={`${translations.REMAINING_CHARACTERS} ${question500MaxLength - values.roomNote.length}`}
                />
                {touched[note] && errors[note] ? (
                    <FormHelperText error={true}>
                        <>{errors[note]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
        </>
    );
};

export const RoomCleanup = (
    setFieldValue: (field: string, value: string | number, shouldValidate?: boolean | undefined) => void,
    id?: number,
) => {
    const idRoom = id ? id : '';
    setFieldValue(`room${idRoom}Type`, '');
    setFieldValue(`room${idRoom}Area`, '');
    setFieldValue(`room${idRoom}IsDamageOnCeiling`, '');
    setFieldValue(`room${idRoom}NumberOfDemagedWalls`, '');
    setFieldValue(`room${idRoom}WallFinishType`, '');
    setFieldValue(`room${idRoom}Note`, '');
};

export default Room;
